import React, { useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, FileCheck, Calendar, Quote } from "lucide-react";
import theme from "../../styles/theme";

const T = theme.colors;

export default function SourcesPanel({ sources }) {
  const [expanded, setExpanded] = useState(false);

  if (!sources || sources.length === 0) return null;

  return (
    <div style={{
      margin: "0 24px 16px",
      borderRadius: 10,
      border: `1px solid ${T.border}`,
      overflow: "hidden",
      background: T.midnight + "90",
    }}>
      {/* Toggle header */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: "100%",
          display: "flex", alignItems: "center", gap: 8,
          padding: "10px 16px",
          background: "transparent",
          border: "none",
          color: T.textMuted,
          fontSize: 11,
          fontFamily: theme.fonts.mono,
          letterSpacing: 1,
          cursor: "pointer",
          transition: "color 0.15s",
        }}
      >
        <FileCheck size={13} color={T.green} />
        <span style={{ color: T.green, fontWeight: 600 }}>
          {sources.length} VERIFIED SOURCE{sources.length > 1 ? "S" : ""}
        </span>
        <span style={{ color: T.textDim, marginLeft: 4 }}>
          — Tap to {expanded ? "collapse" : "review original sources"}
        </span>
        <div style={{ flex: 1 }} />
        {expanded
          ? <ChevronDown size={14} color={T.textDim} />
          : <ChevronRight size={14} color={T.textDim} />
        }
      </button>

      {/* Sources list */}
      {expanded && (
        <div style={{ padding: "0 16px 14px" }}>
          {sources.map((src, i) => (
            <div
              key={i}
              style={{
                padding: "12px 14px",
                marginTop: i === 0 ? 0 : 8,
                borderRadius: 8,
                background: T.surface,
                border: `1px solid ${T.border}40`,
              }}
            >
              {/* Source header */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: 4,
                  background: T.violet + "15", border: `1px solid ${T.violet}25`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, marginTop: 1,
                }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: T.violet, fontFamily: theme.fonts.mono }}>
                    {i + 1}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                      {src.name}
                    </span>
                    {src.type && (
                      <span style={{
                        fontSize: 9, padding: "1px 6px", borderRadius: 3,
                        background: src.type === "primary" ? T.green + "20" : T.blue + "20",
                        color: src.type === "primary" ? T.green : T.blue,
                        fontFamily: theme.fonts.mono, fontWeight: 600, letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}>
                        {src.type}
                      </span>
                    )}
                  </div>
                  {src.publisher && (
                    <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>
                      {src.publisher}
                    </div>
                  )}
                </div>
              </div>

              {/* Access date + link */}
              <div style={{
                display: "flex", alignItems: "center", gap: 12,
                marginBottom: 8, flexWrap: "wrap",
              }}>
                {src.date && (
                  <span style={{
                    display: "flex", alignItems: "center", gap: 4,
                    fontSize: 10, color: T.textDim, fontFamily: theme.fonts.mono,
                  }}>
                    <Calendar size={10} />
                    {src.date}
                  </span>
                )}
                {src.accessed && (
                  <span style={{
                    display: "flex", alignItems: "center", gap: 4,
                    fontSize: 10, color: T.textDim, fontFamily: theme.fonts.mono,
                  }}>
                    Accessed: {src.accessed}
                  </span>
                )}
                {src.url && (
                  <a
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      fontSize: 10, color: T.violet,
                      fontFamily: theme.fonts.mono,
                      textDecoration: "none",
                    }}
                  >
                    <ExternalLink size={10} /> View original
                  </a>
                )}
              </div>

              {/* Summary of what was used */}
              <div style={{
                padding: "8px 12px",
                borderRadius: 6,
                background: T.midnight,
                borderLeft: `3px solid ${T.violet}40`,
              }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 5,
                  marginBottom: 4,
                  fontSize: 9, color: T.textDim,
                  fontFamily: theme.fonts.mono,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}>
                  <Quote size={9} /> What we referenced
                </div>
                <p style={{
                  fontSize: 12, color: T.textMuted,
                  lineHeight: 1.55, margin: 0,
                }}>
                  {src.summary}
                </p>
              </div>

              {/* Specific data points pulled (optional) */}
              {src.dataPoints && src.dataPoints.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{
                    fontSize: 9, color: T.textDim,
                    fontFamily: theme.fonts.mono,
                    letterSpacing: 1, textTransform: "uppercase",
                    marginBottom: 4,
                  }}>
                    Specific data referenced
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {src.dataPoints.map((dp, j) => (
                      <span
                        key={j}
                        style={{
                          fontSize: 10, padding: "2px 8px",
                          borderRadius: 4,
                          background: T.accent + "12",
                          color: T.accent,
                          fontFamily: theme.fonts.mono,
                          border: `1px solid ${T.accent}20`,
                        }}
                      >
                        {dp}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Methodology note */}
          <div style={{
            marginTop: 10, padding: "8px 12px",
            borderRadius: 6,
            background: T.green + "08",
            border: `1px solid ${T.green}15`,
          }}>
            <p style={{
              fontSize: 10, color: T.textDim,
              lineHeight: 1.5, margin: 0,
              fontFamily: theme.fonts.mono,
            }}>
              <span style={{ color: T.green, fontWeight: 600 }}>Transparency note:</span>{" "}
              All Wyzcon Intelligence analyses cite original sources.
              Summaries reflect how each source informed this post — not the source's conclusions.
              We encourage you to review the originals and draw your own conclusions.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
